import { useCallback, useEffect, useState } from "react";

export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Hook to dynamically resize a canvas based on the image and screen size.
 *
 * @param imageUrl - URL of the image to be resized.
 * @returns - An object with the current canvas dimensions, parent and image refs, and current screen size.
 */
export function useResize(
  imgElement: HTMLCanvasElement | undefined,
  parentElement: { width: number; height: number },
  rotateDimensions: boolean,
  isSettingsExpanded?: boolean
) {
  const [canvasDimensions, setCanvasDimensions] = useState<Dimensions>({
    width: parentElement.width,
    height: parentElement.height,
  });

  /**
   * Resizes the container dimensions to fit the specified width, height, and image ratio.
   * If the width-to-height ratio is greater than the image ratio, the width is adjusted to maintain the image ratio.
   * If the width-to-height ratio is less than or equal to the image ratio, the height is adjusted to maintain
   * the image ratio.
   * @param {Object} params - The parameters for resizing the container.
   * @param {number} params.width - The width of the container.
   * @param {number} params.height - The height of the container.
   * @param {number} params.imageRatio - The ratio of the image width to height.
   * @returns {Dimensions} - The adjusted dimensions of the container.
   */
  const fitToContainer = ({
    width,
    height,
    imageRatio,
  }: {
    width: number;
    height: number;
    imageRatio: number;
  }): Dimensions => {
    if (!imageRatio) return { width, height };
    if (width / height > imageRatio) {
      return { width: height * imageRatio, height };
    }

    return { width, height: width / imageRatio };
  };

  // Listener to update screen size when the window is resized
  const updateCanvasSize = useCallback(() => {
    if (!parentElement || !imgElement) return;

    let imageDimensions = {
      width: imgElement.width,
      height: imgElement.height,
    };
    let imageRatio = imageDimensions.width / imageDimensions.height || 0;

    if (rotateDimensions) {
      imageDimensions = {
        width: imgElement.width,
        height: imgElement.height,
      };
      imageRatio = 1 / imageRatio;
    }

    const maxDimensions = parentElement;

    const fittedDimensions = fitToContainer({
      width: maxDimensions.width,
      height: maxDimensions.height,
      imageRatio,
    });

    setCanvasDimensions({
      width: Math.min(Math.ceil(fittedDimensions.width), parentElement.width),
      height: Math.min(Math.ceil(fittedDimensions.height), parentElement.height),
    });
  }, [imgElement, parentElement, rotateDimensions]);

  // Effect to handle screen resizing
  useEffect(() => {
    const onResize = () => updateCanvasSize();
    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [updateCanvasSize]);

  useEffect(() => {
    updateCanvasSize();
  }, [updateCanvasSize, isSettingsExpanded]);

  return { canvasDimensions };
}
